import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Table,
  Tag,
  Drawer,
  Descriptions,
  Divider,
  Progress,
  Spin,
} from "antd";
import { motion } from "framer-motion";
import { Card } from "reactstrap";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/block/Block";
import { formatCurrency } from "../../utils/Utils";
import {
  WarningOutlined,
  BellOutlined,
  RobotOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { ShimmerTableCard } from "../procurement-inventory-control-tower/components/ProcurementShimmerLoader";
import ReactECharts from "echarts-for-react";
import controlTowerApis from "../../api/control-tower/controlTowerApis";

interface ProjectBudgetStatus {
  key: string;
  project: string;
  orgId?: string;
  siteId?: string;
  budget: number;
  committed: number;
  accrued: number;
  paid: number;
  pendingWorkValue: number;
  variance: number;
  utilizationPercent: number;
  forecastOverrun: number;
  flag: "🔴" | "🟠" | "🟢";
}

interface ModuleBudgetDistribution {
  key: string;
  module: string;
  budget: number;
  committed: number;
  accrued: number;
  paid: number;
  pendingWorkValue: number;
  variance: number;
  utilizationPercent: number;
  forecastOverrun: number;
  percentOfTotal: number;
  flag: "🔴" | "🟠" | "🟢";
}

interface ProjectFinancialRisk {
  key: string;
  project: string;
  currentVariance: number;
  forecastOverrun: number;
  procurementCostDrift: number;
  committed: number; // Committed value for calculating procurement drift impact
  paymentDelayImpact: number;
  overheadRate: number; // Daily overhead rate in ₹
  duration: number; // Duration in days
  totalRiskExposure: number;
  flag: "🔴" | "🟠" | "🟢";
}

interface BudgetRiskInterlinking {
  riskId: string;
  project: string;
  riskType: string;
  detectionLogic: string;
  impactArea: string;
  quantifiedImpact: number;
  moduleTrigger: string;
  automatedMitigation: string;
  automatedMitigationSteps?: Array<{
    step: string;
    details?: string[];
    notifications?: string[];
    costSavings?: string;
  }>;
  responsiblePerson: string;
  status: "Active" | "Mitigated" | "Pending";
}

interface ExecutiveSummaryData {
  totalBudget: string;
  committedValue: string;
  committedPercentage: string;
  uncommittedBudget: string;
  uncommittedPercentage: string;
  accruedValue: string;
  accruedPercentage: string;
  paidValue: string;
  paidPercentageOfAccrued: string;
  paidPercentageOfCommitted: string;
  variance: string;
  variancePercentage: string;
  forecastOverrun: string;
  forecastOverrunPercentage: string;
  costUtilization: string;
  pendingWorkValue: string;
  totalProjects: string;
  overallStatus: string;
  riskLevel: string;
}

const BudgetControlTower: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [riskDrawerVisible, setRiskDrawerVisible] = useState(false);
  const [selectedRisks, setSelectedRisks] = useState<BudgetRiskInterlinking[]>(
    []
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [aiLoadingRisks, setAiLoadingRisks] = useState<{
    [key: string]: boolean;
  }>({});
  const [aiLoadingTexts, setAiLoadingTexts] = useState<{
    [key: string]: string;
  }>({});
  const [executiveSummaryData, setExecutiveSummaryData] = useState<ExecutiveSummaryData | null>(null);
  const [projectBudgetStatuses, setProjectBudgetStatuses] = useState<ProjectBudgetStatus[]>([]);
  const [projectFinancialRisks, setProjectFinancialRisks] = useState<ProjectFinancialRisk[]>([]);
  const [timelineUtilizationData, setTimelineUtilizationData] = useState<{
    months: string[];
    plannedUtilization: number[];
    actualAccrual: number[];
    forecast: number[];
    warnings: Array<{ type: string; month: string; message: string; value: number; difference?: number }>;
  } | null>(null);
  const [executiveInsight, setExecutiveInsight] = useState<string>("");
  const [moduleBudgetData, setModuleBudgetData] = useState<{ [key: string]: ModuleBudgetDistribution[] }>({});
  const [moduleLoading, setModuleLoading] = useState<{ [key: string]: boolean }>({});

  const aiLoadingMessages = [
    "Analyzing budget variances...",
    "Checking procurement trends...",
    "Fetching optimal solutions...",
    "Processing financial constraints...",
    "Calculating impact...",
    "Generating recommendations...",
  ];

  // Helper function to map flag string to emoji
  const mapFlagToEmoji = (flag: string): "🔴" | "🟠" | "🟢" => {
    if (flag?.toLowerCase() === "red") return "🔴";
    if (flag?.toLowerCase() === "amber" || flag?.toLowerCase() === "orange") return "🟠";
    return "🟢";
  };

  // Mock data (fallback)
  const mockProjectBudgetStatuses: ProjectBudgetStatus[] = [
    {
      key: "1",
      project: "Grandeur Heights",
      budget: 50000000, // ₹5 Cr
      committed: 46000000,
      accrued: 38000000,
      paid: 34500000,
      pendingWorkValue: 12000000, // ₹1.2 Cr pending work
      variance: 12000000, // Budget - Accrued = 5 Cr - 3.8 Cr = ₹1.2 Cr
      utilizationPercent: 76, // (3.8 / 5) × 100 = 76%
      forecastOverrun: 0, // PendingWorkValue - Variance = 1.2 Cr - 1.2 Cr = 0 (no overrun expected)
      flag: "🟢",
    },
    {
      key: "2",
      project: "Paramount Rise",
      budget: 38000000, // ₹3.8 Cr
      committed: 37500000,
      accrued: 35500000,
      paid: 32000000,
      pendingWorkValue: 3500000, // ₹35L pending work
      variance: 2500000, // Budget - Accrued = 3.8 Cr - 3.55 Cr = ₹25L
      utilizationPercent: 93, // (3.55 / 3.8) × 100 = 93%
      forecastOverrun: 1000000, // PendingWorkValue - Variance = 35L - 25L = ₹10L overrun expected
      flag: "🟠",
    },
    {
      key: "3",
      project: "Elysium Towers",
      budget: 75000000, // ₹7.5 Cr
      committed: 78000000,
      accrued: 80250000,
      paid: 72000000,
      pendingWorkValue: 2000000, // ₹20L more work still pending
      variance: -5250000, // Budget - Accrued = 7.5 Cr - 8.025 Cr = -₹52.5L (already overrun)
      utilizationPercent: 107, // (8.025 / 7.5) × 100 = 107%
      forecastOverrun: 7250000, // PendingWorkValue - Variance = 20L - (-52.5L) = 20L + 52.5L = ₹72.5L total overrun at completion
      flag: "🔴",
    },
    {
      key: "4",
      project: "Skyline Royale",
      budget: 45000000, // ₹4.5 Cr
      committed: 42000000,
      accrued: 36000000,
      paid: 31000000,
      pendingWorkValue: 9000000, // ₹90L pending work
      variance: 9000000, // Budget - Accrued = 4.5 Cr - 3.6 Cr = ₹90L
      utilizationPercent: 80, // (3.6 / 4.5) × 100 = 80%
      forecastOverrun: 0, // PendingWorkValue - Variance = 90L - 90L = 0 (exactly on budget)
      flag: "🟢",
    },
    {
      key: "5",
      project: "Opéra Sky",
      budget: 32000000, // ₹3.2 Cr
      committed: 30000000,
      accrued: 28000000,
      paid: 25000000,
      pendingWorkValue: 4500000, // ₹45L pending work
      variance: 4000000, // Budget - Accrued = 3.2 Cr - 2.8 Cr = ₹40L
      utilizationPercent: 87, // (2.8 / 3.2) × 100 = 87%
      forecastOverrun: 500000, // PendingWorkValue - Variance = 45L - 40L = ₹5L overrun expected
      flag: "🟠",
    },
  ];

  // Module-wise Budget Distribution per Project
  const projectModuleBudgets: { [key: string]: ModuleBudgetDistribution[] } = {
    "1": [
      // Grandeur Heights modules
      {
        key: "1-1",
        module: "Procurement (Material)",
        budget: 22500000, // 45% of ₹5 Cr
        committed: 20700000,
        accrued: 17100000,
        paid: 15525000,
        pendingWorkValue: 5400000,
        variance: 5400000,
        utilizationPercent: 76,
        forecastOverrun: 0,
        percentOfTotal: 45,
        flag: "🟢",
      },
      {
        key: "1-2",
        module: "Work Orders (Labor)",
        budget: 23500000, // 47% of ₹5 Cr
        committed: 21620000,
        accrued: 17860000,
        paid: 16215000,
        pendingWorkValue: 5640000,
        variance: 5640000,
        utilizationPercent: 76,
        forecastOverrun: 0,
        percentOfTotal: 47,
        flag: "🟢",
      },
      {
        key: "1-3",
        module: "Inventory Holding Cost",
        budget: 1000000, // 2% of ₹5 Cr
        committed: 920000,
        accrued: 760000,
        paid: 690000,
        pendingWorkValue: 240000,
        variance: 240000,
        utilizationPercent: 76,
        forecastOverrun: 0,
        percentOfTotal: 2,
        flag: "🟢",
      },
      {
        key: "1-4",
        module: "Quality & Rework",
        budget: 2000000, // 4% of ₹5 Cr
        committed: 1840000,
        accrued: 1520000,
        paid: 1380000,
        pendingWorkValue: 480000,
        variance: 480000,
        utilizationPercent: 76,
        forecastOverrun: 0,
        percentOfTotal: 4,
        flag: "🟢",
      },
      {
        key: "1-5",
        module: "Contingencies",
        budget: 1000000, // 2% of ₹5 Cr
        committed: 920000,
        accrued: 760000,
        paid: 690000,
        pendingWorkValue: 240000,
        variance: 240000,
        utilizationPercent: 76,
        forecastOverrun: 0,
        percentOfTotal: 2,
        flag: "🟢",
      },
    ],
    "2": [
      // Paramount Rise modules
      {
        key: "2-1",
        module: "Procurement (Material)",
        budget: 17100000, // 45% of ₹3.8 Cr
        committed: 16875000,
        accrued: 15975000,
        paid: 14400000,
        pendingWorkValue: 1575000,
        variance: 1125000,
        utilizationPercent: 93,
        forecastOverrun: 450000,
        percentOfTotal: 45,
        flag: "🟠",
      },
      {
        key: "2-2",
        module: "Work Orders (Labor)",
        budget: 17860000, // 47% of ₹3.8 Cr
        committed: 17625000,
        accrued: 16685000,
        paid: 15040000,
        pendingWorkValue: 1645000,
        variance: 1175000,
        utilizationPercent: 93,
        forecastOverrun: 470000,
        percentOfTotal: 47,
        flag: "🟠",
      },
      {
        key: "2-3",
        module: "Inventory Holding Cost",
        budget: 760000, // 2% of ₹3.8 Cr
        committed: 750000,
        accrued: 710000,
        paid: 640000,
        pendingWorkValue: 70000,
        variance: 50000,
        utilizationPercent: 93,
        forecastOverrun: 20000,
        percentOfTotal: 2,
        flag: "🟢",
      },
      {
        key: "2-4",
        module: "Quality & Rework",
        budget: 1520000, // 4% of ₹3.8 Cr
        committed: 1500000,
        accrued: 1420000,
        paid: 1280000,
        pendingWorkValue: 140000,
        variance: 100000,
        utilizationPercent: 93,
        forecastOverrun: 40000,
        percentOfTotal: 4,
        flag: "🔴",
      },
      {
        key: "2-5",
        module: "Contingencies",
        budget: 760000, // 2% of ₹3.8 Cr
        committed: 750000,
        accrued: 710000,
        paid: 640000,
        pendingWorkValue: 70000,
        variance: 50000,
        utilizationPercent: 93,
        forecastOverrun: 20000,
        percentOfTotal: 2,
        flag: "🟢",
      },
    ],
    "3": [
      // Elysium Towers modules (overrun scenario)
      {
        key: "3-1",
        module: "Procurement (Material)",
        budget: 33750000, // 45% of ₹7.5 Cr
        committed: 35100000,
        accrued: 36112500,
        paid: 32400000,
        pendingWorkValue: 900000,
        variance: -2362500,
        utilizationPercent: 107,
        forecastOverrun: 3262500,
        percentOfTotal: 45,
        flag: "🔴",
      },
      {
        key: "3-2",
        module: "Work Orders (Labor)",
        budget: 35250000, // 47% of ₹7.5 Cr
        committed: 36660000,
        accrued: 37717500,
        paid: 33840000,
        pendingWorkValue: 940000,
        variance: -2467500,
        utilizationPercent: 107,
        forecastOverrun: 3407500,
        percentOfTotal: 47,
        flag: "🔴",
      },
      {
        key: "3-3",
        module: "Inventory Holding Cost",
        budget: 1500000, // 2% of ₹7.5 Cr
        committed: 1560000,
        accrued: 1605000,
        paid: 1440000,
        pendingWorkValue: 40000,
        variance: -105000,
        utilizationPercent: 107,
        forecastOverrun: 145000,
        percentOfTotal: 2,
        flag: "🟠",
      },
      {
        key: "3-4",
        module: "Quality & Rework",
        budget: 3000000, // 4% of ₹7.5 Cr
        committed: 3120000,
        accrued: 3210000,
        paid: 2880000,
        pendingWorkValue: 80000,
        variance: -210000,
        utilizationPercent: 107,
        forecastOverrun: 290000,
        percentOfTotal: 4,
        flag: "🔴",
      },
      {
        key: "3-5",
        module: "Contingencies",
        budget: 1500000, // 2% of ₹7.5 Cr
        committed: 1560000,
        accrued: 1605000,
        paid: 1440000,
        pendingWorkValue: 40000,
        variance: -105000,
        utilizationPercent: 107,
        forecastOverrun: 145000,
        percentOfTotal: 2,
        flag: "🟠",
      },
    ],
    "4": [
      // Skyline Royale modules
      {
        key: "4-1",
        module: "Procurement (Material)",
        budget: 20250000, // 45% of ₹4.5 Cr
        committed: 18900000,
        accrued: 16200000,
        paid: 13950000,
        pendingWorkValue: 4050000,
        variance: 4050000,
        utilizationPercent: 80,
        forecastOverrun: 0,
        percentOfTotal: 45,
        flag: "🟢",
      },
      {
        key: "4-2",
        module: "Work Orders (Labor)",
        budget: 21150000, // 47% of ₹4.5 Cr
        committed: 19740000,
        accrued: 16920000,
        paid: 14570000,
        pendingWorkValue: 4230000,
        variance: 4230000,
        utilizationPercent: 80,
        forecastOverrun: 0,
        percentOfTotal: 47,
        flag: "🟢",
      },
      {
        key: "4-3",
        module: "Inventory Holding Cost",
        budget: 900000, // 2% of ₹4.5 Cr
        committed: 840000,
        accrued: 720000,
        paid: 620000,
        pendingWorkValue: 180000,
        variance: 180000,
        utilizationPercent: 80,
        forecastOverrun: 0,
        percentOfTotal: 2,
        flag: "🟢",
      },
      {
        key: "4-4",
        module: "Quality & Rework",
        budget: 1800000, // 4% of ₹4.5 Cr
        committed: 1680000,
        accrued: 1440000,
        paid: 1240000,
        pendingWorkValue: 360000,
        variance: 360000,
        utilizationPercent: 80,
        forecastOverrun: 0,
        percentOfTotal: 4,
        flag: "🟢",
      },
      {
        key: "4-5",
        module: "Contingencies",
        budget: 900000, // 2% of ₹4.5 Cr
        committed: 840000,
        accrued: 720000,
        paid: 620000,
        pendingWorkValue: 180000,
        variance: 180000,
        utilizationPercent: 80,
        forecastOverrun: 0,
        percentOfTotal: 2,
        flag: "🟢",
      },
    ],
    "5": [
      // Opéra Sky modules
      {
        key: "5-1",
        module: "Procurement (Material)",
        budget: 14400000, // 45% of ₹3.2 Cr
        committed: 13500000,
        accrued: 12600000,
        paid: 11250000,
        pendingWorkValue: 2025000,
        variance: 1800000,
        utilizationPercent: 87,
        forecastOverrun: 225000,
        percentOfTotal: 45,
        flag: "🟠",
      },
      {
        key: "5-2",
        module: "Work Orders (Labor)",
        budget: 15040000, // 47% of ₹3.2 Cr
        committed: 14100000,
        accrued: 13160000,
        paid: 11750000,
        pendingWorkValue: 2115000,
        variance: 1880000,
        utilizationPercent: 87,
        forecastOverrun: 235000,
        percentOfTotal: 47,
        flag: "🟠",
      },
      {
        key: "5-3",
        module: "Inventory Holding Cost",
        budget: 640000, // 2% of ₹3.2 Cr
        committed: 600000,
        accrued: 560000,
        paid: 500000,
        pendingWorkValue: 90000,
        variance: 80000,
        utilizationPercent: 87,
        forecastOverrun: 10000,
        percentOfTotal: 2,
        flag: "🟢",
      },
      {
        key: "5-4",
        module: "Quality & Rework",
        budget: 1280000, // 4% of ₹3.2 Cr
        committed: 1200000,
        accrued: 1120000,
        paid: 1000000,
        pendingWorkValue: 180000,
        variance: 160000,
        utilizationPercent: 87,
        forecastOverrun: 20000,
        percentOfTotal: 4,
        flag: "🔴",
      },
      {
        key: "5-5",
        module: "Contingencies",
        budget: 640000, // 2% of ₹3.2 Cr
        committed: 600000,
        accrued: 560000,
        paid: 500000,
        pendingWorkValue: 90000,
        variance: 80000,
        utilizationPercent: 87,
        forecastOverrun: 10000,
        percentOfTotal: 2,
        flag: "🟢",
      },
    ],
  };

  const mockProjectFinancialRisks: ProjectFinancialRisk[] = [
    {
      key: "1",
      project: "Grandeur Heights",
      currentVariance: 12000000,
      forecastOverrun: 0,
      procurementCostDrift: 0,
      committed: 46000000, // ₹4.6 Cr
      paymentDelayImpact: 5,
      overheadRate: 50000, // ₹50K per day site overhead
      duration: 365, // 365 days project duration
      // Total Risk Exposure = forecastOverrun + (procurementCostDrift% × committed) + (paymentDelayImpact% × overheadRate × duration)
      // = 0 + (0% × 46M) + (5% × 50000 × 365) = 0 + 0 + 912,500 = ₹9.125L
      totalRiskExposure: 912500,
      flag: "🟢",
    },
    {
      key: "2",
      project: "Paramount Rise",
      currentVariance: 2500000,
      forecastOverrun: 1000000,
      procurementCostDrift: 3,
      committed: 37500000, // ₹3.75 Cr
      paymentDelayImpact: 10,
      overheadRate: 40000, // ₹40K per day
      duration: 300, // 300 days
      // = 1,000,000 + (3% × 37.5M) + (10% × 40000 × 300) = 1M + 1.125M + 1.2M = ₹33.25L
      totalRiskExposure: 3325000,
      flag: "🟠",
    },
    {
      key: "3",
      project: "Elysium Towers",
      currentVariance: -5250000, // Already overrun by ₹52.5L
      forecastOverrun: 7250000, // Updated: PendingWorkValue (₹20L) - Variance (-₹52.5L) = ₹72.5L total overrun at completion
      procurementCostDrift: 5,
      committed: 78000000, // ₹7.8 Cr (exceeds budget)
      paymentDelayImpact: 15,
      overheadRate: 60000, // ₹60K per day
      duration: 450, // 450 days
      // = 7,250,000 + (5% × 78M) + (15% × 60000 × 450) = 7.25M + 3.9M + 4.05M = ₹1.52 Cr
      totalRiskExposure: 15200000,
      flag: "🔴",
    },
    {
      key: "4",
      project: "Skyline Royale",
      currentVariance: 9000000,
      forecastOverrun: 0,
      procurementCostDrift: 1,
      committed: 42000000, // ₹4.2 Cr
      paymentDelayImpact: 6,
      overheadRate: 30000, // ₹30K per day
      duration: 200, // 200 days
      // = 0 + (1% × 42M) + (6% × 30000 × 200) = 0 + 420,000 + 360,000 = ₹7.8L
      totalRiskExposure: 780000,
      flag: "🟢",
    },
    {
      key: "5",
      project: "Opéra Sky",
      currentVariance: 4000000,
      forecastOverrun: 500000,
      procurementCostDrift: 2,
      committed: 30000000, // ₹3 Cr
      paymentDelayImpact: 8,
      overheadRate: 40000, // ₹40K per day
      duration: 300, // 300 days
      // = 500,000 + (2% × 30M) + (8% × 40000 × 300) = 500K + 600K + 960K = ₹20.6L
      totalRiskExposure: 2060000,
      flag: "🟠",
    },
  ];

  const mockRiskData: { [projectKey: string]: BudgetRiskInterlinking[] } = {
    "3": [
      {
        riskId: "RISK-001",
        project: "Elysium Towers",
        riskType: "Budget Overrun",
        detectionLogic: "Accrued > Budget",
        impactArea: "Project Budget, Cash Flow",
        quantifiedImpact: 4500000,
        moduleTrigger: "Work Order, Procurement",
        automatedMitigation:
          "Freeze new WOs/PRs + Review cost overruns + Rebalance budget allocation",
        automatedMitigationSteps: [
          {
            step: "Freeze New Work Orders & Purchase Requests",
            details: [
              "Current Budget: ₹7.5 Cr",
              "Accrued Value: ₹7.05 Cr (107% utilization - OVERRUN)",
              "Variance: -₹45L (negative variance indicates budget exceeded)",
              "Action: Immediate freeze on all new WO/PR issuance",
              "Exception Process: Only CFO-approved emergency requests allowed",
              "Review Period: 30 days or until budget rebalanced",
            ],
            notifications: [
              "Notify Project Director (Elysium Towers) - Budget freeze effective immediately",
              "Alert Procurement Team - No new PRs for Elysium Towers without CFO approval",
              "Inform PMO - Budget overrun escalation & recovery plan required",
              "Send CFO dashboard alert - ₹45L overrun on Elysium Towers",
            ],
          },
          {
            step: "Conduct Cost Overrun Review & Root Cause Analysis",
            details: [
              "Review Module-wise Spending:",
              "  - Procurement (Material): 45% of total (₹3.4 Cr committed vs ₹3.05 Cr accrued)",
              "  - Work Orders (Labor): 47% of total (₹3.6 Cr committed vs ₹3.25 Cr accrued)",
              "  - Quality & Rework: 4% of total (₹3L committed & accrued - OVER TARGET >3%)",
              "Root Cause: Quality & Rework at 4% (exceeds 3% threshold)",
              "Secondary Cause: Procurement rate escalation (5% drift detected)",
              "Identify Scope Creep: Review change orders & unapproved scope additions",
            ],
            notifications: [
              "Schedule emergency review meeting with Project Team (within 48 hours)",
              "Request detailed variance report from Finance Team",
              "Alert Quality Manager - Rework cost exceeding acceptable limits",
              "Escalate to CEO/Board if overrun >10% (current: 7% - approaching threshold)",
            ],
          },
          {
            step: "Implement Budget Rebalancing & Cost Control Measures",
            details: [
              "Rebalance Strategy:",
              "  - Transfer ₹20L from Grandeur Heights uncommitted budget (₹1.2 Cr available)",
              "  - Defer non-critical scope items worth ₹15L to Phase 2",
              "  - Negotiate rate reduction with top 3 suppliers (target: 3-5% savings)",
              "  - Reduce Quality & Rework budget by implementing stricter QC protocols",
              "Revised Forecast Overrun: ₹30L → ₹10L (after rebalancing)",
              "Contingency Reserve: Activate ₹15L from Contingencies budget",
            ],
            notifications: [
              "Notify CFO - Budget rebalancing proposal for approval",
              "Alert Grandeur Heights Project Manager - Budget transfer request (₹20L)",
              "Inform Finance Team - Update budget allocations across projects",
              "Send revised budget forecast to PMO & Stakeholders",
            ],
            costSavings:
              "₹20L (budget rebalancing + scope deferral + cost optimization)",
          },
          {
            step: "Monitor & Track Cost Control Implementation",
            details: [
              "Daily Budget Tracking: Monitor all expenditures >₹50K",
              "Weekly Review: Project Director + Finance Team budget sync",
              "Milestone Gates: Approval required at each 10% budget increment",
              "Early Warning: Flag any variance >2% immediately",
              "Recovery Timeline: 60 days to bring variance within acceptable range (<5%)",
            ],
            notifications: [
              "Setup daily budget alert dashboard for Elysium Towers",
              "Schedule weekly cost control review meetings (every Monday)",
              "Assign dedicated Finance Analyst for Elysium Towers tracking",
              "Auto-alert CFO if variance increases beyond current levels",
            ],
          },
        ],
        responsiblePerson: "CFO + Project Director",
        status: "Active",
      },
      {
        riskId: "RISK-002",
        project: "Elysium Towers",
        riskType: "Procurement Cost Escalation",
        detectionLogic: "Latest PO Rate > Avg Rate × 1.1",
        impactArea: "Budget Variance",
        quantifiedImpact: 1500000,
        moduleTrigger: "Procurement",
        automatedMitigation: "Enable alternate supplier + Renegotiate contract",
        automatedMitigationSteps: [
          {
            step: "Identify Cost Escalation Sources",
            details: [
              "Procurement Cost Drift: 5% above baseline",
              "Top 3 Material Categories with Escalation:",
              "  1. Steel/Rebar: +8% rate increase (₹45L impact)",
              "  2. Cement: +6% rate increase (₹28L impact)",
              "  3. Electrical Materials: +12% rate increase (₹38L impact)",
              "Total Escalation Impact: ₹1.11 Cr (cumulative)",
            ],
          },
          {
            step: "Activate Alternate Supplier Strategy",
            details: [
              "For Steel/Rebar:",
              "  - Current Supplier: XYZ Steel (₹58/kg - 8% higher)",
              "  - Alternate Supplier: Jindal TMT (₹54/kg - competitive rate)",
              "  - Estimated Savings: ₹18L (for remaining procurement)",
              "For Cement:",
              "  - Renegotiate bulk pricing with UltraTech",
              "  - Consider ACC Cement as alternate (5% lower)",
              "For Electrical:",
              "  - Switch to Havells/Polycab (competitive pricing)",
            ],
            notifications: [
              "Notify Procurement Head - Urgent supplier diversification",
              "Alert Project Team - Material cost optimization in progress",
              "Escalate to CFO - Procurement strategy adjustment approved",
            ],
            costSavings: "₹35L (alternate supplier + renegotiation)",
          },
        ],
        responsiblePerson: "Procurement Head",
        status: "Active",
      },
    ],
    "2": [
      {
        riskId: "RISK-003",
        project: "Paramount Rise",
        riskType: "High Budget Utilization (>90%)",
        detectionLogic: "Utilization % > 90%",
        impactArea: "Budget Constraint, Future Commitments",
        quantifiedImpact: 2500000,
        moduleTrigger: "Budget Planning",
        automatedMitigation:
          "Hold low-priority POs + Review upcoming commitments",
        automatedMitigationSteps: [
          {
            step: "Review Upcoming Commitments & Prioritize",
            details: [
              "Current Utilization: 93% (₹3.55 Cr accrued of ₹3.8 Cr budget)",
              "Uncommitted Budget: ₹2.5L (only 7% remaining)",
              "Forecast Overrun Risk: ₹10L if current trend continues",
              "Action: Review all pending POs/WOs for priority classification",
            ],
            notifications: [
              "Notify Project Manager - Budget nearing limit (93% utilized)",
              "Alert Procurement Team - Hold non-critical POs pending approval",
              "Inform PMO - Budget constraint on Paramount Rise project",
            ],
          },
          {
            step: "Hold Low-Priority Purchase Orders",
            details: [
              "Pending POs Identified:",
              "  - PO-2025-145 (Landscaping materials - ₹8L) - HOLD (non-critical)",
              "  - PO-2025-158 (Interior finishing - ₹12L) - HOLD (can defer 30 days)",
              "  - PO-2025-162 (HVAC equipment - ₹15L) - PRIORITY (proceed)",
              "Total Deferred: ₹20L",
              "Budget Relief: Extends runway by 2-3 weeks",
            ],
            notifications: [
              "Notify suppliers of deferred POs with revised timelines",
              "Alert Finance Team - Budget preservation actions taken",
              "Update Project Schedule for deferred scope items",
            ],
            costSavings: "₹20L (deferred commitments + budget preservation)",
          },
        ],
        responsiblePerson: "Project Manager + Finance Head",
        status: "Pending",
      },
    ],
  };

  // Module-specific risk data for forecast overruns
  const mockModuleRiskData: { [moduleKey: string]: BudgetRiskInterlinking[] } = {
    "2-1": [
      {
        riskId: "MODULE-RISK-001",
        project: "Paramount Rise - Procurement (Material)",
        riskType: "Module Forecast Overrun",
        detectionLogic: "Module Forecast Overrun > ₹0",
        impactArea: "Material Budget, Project Timeline",
        quantifiedImpact: 450000,
        moduleTrigger: "Procurement (Material)",
        automatedMitigation: "Rate negotiation + Alternate suppliers + Material optimization",
        automatedMitigationSteps: [
          {
            step: "Analyze Material Cost Escalation",
            details: [
              "Current Budget: ₹1.71 Cr",
              "Accrued Value: ₹1.60 Cr (93% utilization)",
              "Forecast Overrun: ₹4.5L",
              "Root Cause: Material rate escalation (3-5% increase detected)",
              "High-impact materials: Steel, Cement, Electrical components",
            ],
          },
          {
            step: "Initiate Supplier Rate Renegotiation",
            details: [
              "Contact top 3 suppliers for rate revision",
              "Request bulk discount for consolidated orders",
              "Negotiate payment terms (early payment discount 2-3%)",
              "Target savings: ₹2L (through renegotiation)",
            ],
            notifications: [
              "Notify Procurement Manager - Urgent rate renegotiation required",
              "Alert Project Manager - Material cost optimization in progress",
              "Inform CFO - Budget relief measures initiated",
            ],
            costSavings: "₹2L (supplier renegotiation)",
          },
          {
            step: "Activate Alternate Supplier Database",
            details: [
              "Identify 2-3 alternate suppliers per material category",
              "Compare pricing and delivery timelines",
              "Initiate trial orders with competitive suppliers",
              "Expected savings: ₹1.5L (alternate sourcing)",
            ],
            notifications: [
              "Alert Procurement Team - Activate alternate supplier matrix",
              "Notify Quality Team - Approve alternate materials if needed",
            ],
            costSavings: "₹1.5L (alternate suppliers)",
          },
          {
            step: "Material Optimization & Waste Reduction",
            details: [
              "Review material consumption patterns",
              "Implement just-in-time procurement",
              "Reduce waste through better planning (target: 2-3% reduction)",
              "Optimize material specifications (avoid over-specification)",
              "Expected savings: ₹1L (waste reduction + optimization)",
            ],
            notifications: [
              "Notify Site Engineer - Material optimization plan activated",
              "Alert Project Team - Review material usage patterns",
            ],
            costSavings: "₹1L (optimization + waste reduction)",
          },
        ],
        responsiblePerson: "Procurement Manager + Project Manager",
        status: "Active",
      },
    ],
    "2-2": [
      {
        riskId: "MODULE-RISK-002",
        project: "Paramount Rise - Work Orders (Labor)",
        riskType: "Module Forecast Overrun",
        detectionLogic: "Module Forecast Overrun > ₹0",
        impactArea: "Labor Budget, Work Order Execution",
        quantifiedImpact: 470000,
        moduleTrigger: "Work Orders (Labor)",
        automatedMitigation: "Workforce optimization + Rate control + Productivity improvement",
        automatedMitigationSteps: [
          {
            step: "Review Labor Rate Escalation",
            details: [
              "Current Budget: ₹1.79 Cr",
              "Accrued Value: ₹1.67 Cr (93% utilization)",
              "Forecast Overrun: ₹4.7L",
              "Root Cause: Labor rate increase + overtime costs",
              "Action: Review all active work orders for rate optimization",
            ],
          },
          {
            step: "Workforce Optimization",
            details: [
              "Analyze workforce deployment efficiency",
              "Reduce overtime hours (target: 15-20% reduction)",
              "Optimize crew size for each work package",
              "Cross-train workers for multi-skill deployment",
              "Expected savings: ₹2.5L (workforce optimization)",
            ],
            notifications: [
              "Notify Site Supervisor - Workforce optimization plan",
              "Alert HR Team - Review labor deployment strategy",
              "Inform Project Manager - Overtime control measures",
            ],
            costSavings: "₹2.5L (workforce optimization + overtime reduction)",
          },
          {
            step: "Productivity Enhancement Measures",
            details: [
              "Implement better work planning and scheduling",
              "Provide necessary tools and equipment upfront",
              "Reduce idle time through better coordination",
              "Target: 10-15% productivity improvement",
              "Expected savings: ₹1.5L (productivity gains)",
            ],
            notifications: [
              "Notify Project Team - Productivity improvement program",
              "Alert Site Engineer - Enhanced work planning required",
            ],
            costSavings: "₹1.5L (productivity improvement)",
          },
          {
            step: "Work Order Rate Control",
            details: [
              "Freeze new work orders at inflated rates",
              "Renegotiate rates with labor contractors",
              "Bundle work packages for better rates",
              "Expected savings: ₹0.7L (rate control)",
            ],
            notifications: [
              "Notify Contracts Team - Work order rate control",
              "Alert CFO - Labor cost control measures active",
            ],
            costSavings: "₹0.7L (rate negotiation)",
          },
        ],
        responsiblePerson: "Project Manager + Site Supervisor",
        status: "Active",
      },
    ],
    "3-1": [
      {
        riskId: "MODULE-RISK-003",
        project: "Elysium Towers - Procurement (Material)",
        riskType: "Critical Module Overrun",
        detectionLogic: "Module Forecast Overrun > ₹1L & Module Over Budget",
        impactArea: "Material Budget, Project Budget",
        quantifiedImpact: 3262500,
        moduleTrigger: "Procurement (Material)",
        automatedMitigation: "Emergency cost control + Supplier diversification + Scope adjustment",
        automatedMitigationSteps: [
          {
            step: "Critical Situation Analysis",
            details: [
              "Current Budget: ₹3.38 Cr",
              "Accrued Value: ₹3.61 Cr (107% - OVERRUN)",
              "Forecast Overrun: ₹32.6L (CRITICAL)",
              "Status: Already exceeded budget by ₹23.6L",
              "Action: Emergency intervention required",
            ],
          },
          {
            step: "Immediate Cost Freeze & Review",
            details: [
              "Freeze all new material orders immediately",
              "Review all pending purchase orders >₹1L",
              "Identify non-critical materials that can be deferred",
              "Emergency meeting with Project Director & CFO",
              "Timeline: 24-48 hours for decision",
            ],
            notifications: [
              "URGENT: Notify CFO - Critical budget overrun on Elysium Towers",
              "URGENT: Alert Project Director - Material budget freeze",
              "Notify Procurement Head - Emergency cost control activated",
              "Escalate to CEO - Budget overrun >10% threshold",
            ],
          },
          {
            step: "Supplier Diversification & Rate Revision",
            details: [
              "Identify materials with highest cost escalation",
              "Source 2-3 alternate suppliers immediately",
              "Renegotiate contracts with existing suppliers",
              "Bulk order consolidation for better rates",
              "Target savings: ₹8-10L (supplier optimization)",
            ],
            notifications: [
              "Notify Procurement Team - Emergency supplier sourcing",
              "Alert Legal Team - Fast-track supplier agreements",
              "Inform CFO - Supplier cost optimization plan",
            ],
            costSavings: "₹8-10L (supplier optimization + rate negotiation)",
          },
          {
            step: "Scope Adjustment & Value Engineering",
            details: [
              "Review material specifications for cost reduction",
              "Identify alternate materials with same performance",
              "Defer non-critical material purchases",
              "Value engineering: Reduce over-specification",
              "Target savings: ₹5-7L (value engineering)",
            ],
            notifications: [
              "Notify Design Team - Value engineering review required",
              "Alert Project Team - Scope optimization plan",
              "Inform Stakeholders - Material specification adjustments",
            ],
            costSavings: "₹5-7L (value engineering + scope adjustment)",
          },
          {
            step: "Budget Reallocation & Contingency Activation",
            details: [
              "Request budget transfer from other projects",
              "Activate contingency reserve (₹15L available)",
              "Seek additional budget approval if needed",
              "Revised forecast after mitigation: ₹32.6L → ₹10-15L",
            ],
            notifications: [
              "Notify CFO - Budget reallocation request",
              "Alert Finance Team - Contingency activation",
              "Inform Stakeholders - Budget adjustment plan",
            ],
          },
        ],
        responsiblePerson: "CFO + Project Director + Procurement Head",
        status: "Active",
      },
    ],
    "3-2": [
      {
        riskId: "MODULE-RISK-004",
        project: "Elysium Towers - Work Orders (Labor)",
        riskType: "Critical Module Overrun",
        detectionLogic: "Module Forecast Overrun > ₹1L & Module Over Budget",
        impactArea: "Labor Budget, Project Budget",
        quantifiedImpact: 3407500,
        moduleTrigger: "Work Orders (Labor)",
        automatedMitigation: "Work order freeze + Rate control + Workforce restructuring",
        automatedMitigationSteps: [
          {
            step: "Critical Labor Cost Analysis",
            details: [
              "Current Budget: ₹3.53 Cr",
              "Accrued Value: ₹3.77 Cr (107% - OVERRUN)",
              "Forecast Overrun: ₹34.1L (CRITICAL)",
              "Status: Already exceeded budget by ₹24.7L",
              "Root Cause: Rate escalation + extended duration + overtime",
            ],
          },
          {
            step: "Emergency Work Order Freeze",
            details: [
              "Freeze all new work order issuance",
              "Review all active work orders for completion status",
              "Cancel or defer non-critical work packages",
              "Only emergency/safety-critical work allowed",
              "CFO approval required for any new work orders",
            ],
            notifications: [
              "URGENT: Notify Project Director - Work order freeze active",
              "URGENT: Alert Site Supervisor - No new WO issuance",
              "Notify CFO - Critical labor budget overrun",
              "Escalate to Management - Emergency cost control",
            ],
          },
          {
            step: "Labor Rate Control & Contractor Renegotiation",
            details: [
              "Renegotiate rates with all labor contractors",
              "Implement strict no-overtime policy",
              "Bundle remaining work for better rates",
              "Review contractor performance and costs",
              "Target savings: ₹10-12L (rate control)",
            ],
            notifications: [
              "Notify Contracts Manager - Emergency rate renegotiation",
              "Alert Site Supervisor - Overtime control measures",
              "Inform HR - Contractor rate optimization",
            ],
            costSavings: "₹10-12L (rate control + overtime elimination)",
          },
          {
            step: "Workforce Restructuring & Efficiency",
            details: [
              "Optimize crew sizes for maximum efficiency",
              "Eliminate idle time through better planning",
              "Cross-utilization of workers across tasks",
              "Improve productivity through better tools/equipment",
              "Target savings: ₹6-8L (efficiency improvements)",
            ],
            notifications: [
              "Notify Site Engineer - Workforce optimization plan",
              "Alert Project Team - Productivity improvement program",
            ],
            costSavings: "₹6-8L (workforce efficiency)",
          },
          {
            step: "Work Scope Rationalization",
            details: [
              "Defer non-critical work to later phases",
              "Eliminate redundant or low-value activities",
              "Fast-track critical path to reduce duration",
              "Target savings: ₹4-6L (scope rationalization)",
            ],
            notifications: [
              "Notify Project Manager - Scope rationalization plan",
              "Alert Stakeholders - Work package adjustments",
              "Inform CFO - Labor cost recovery measures",
            ],
            costSavings: "₹4-6L (scope optimization)",
          },
        ],
        responsiblePerson: "Project Director + Site Supervisor + Contracts Manager",
        status: "Active",
      },
    ],
    "5-1": [
      {
        riskId: "MODULE-RISK-005",
        project: "Opéra Sky - Procurement (Material)",
        riskType: "Module Forecast Overrun",
        detectionLogic: "Module Forecast Overrun > ₹0",
        impactArea: "Material Budget",
        quantifiedImpact: 225000,
        moduleTrigger: "Procurement (Material)",
        automatedMitigation: "Supplier rate control + Material optimization + Alternate sourcing",
        automatedMitigationSteps: [
          {
            step: "Material Cost Review",
            details: [
              "Current Budget: ₹1.44 Cr",
              "Accrued Value: ₹1.26 Cr (87% utilization)",
              "Forecast Overrun: ₹2.25L",
              "Action: Preventive cost control measures",
            ],
          },
          {
            step: "Supplier Rate Negotiation",
            details: [
              "Renegotiate rates with current suppliers",
              "Request volume-based discounts",
              "Explore payment term benefits",
              "Target savings: ₹1.2L (rate negotiation)",
            ],
            notifications: [
              "Notify Procurement Manager - Rate renegotiation",
              "Alert Project Manager - Cost control initiated",
            ],
            costSavings: "₹1.2L (supplier negotiation)",
          },
          {
            step: "Material Optimization",
            details: [
              "Review material specifications",
              "Reduce wastage through better planning",
              "Optimize inventory levels",
              "Target savings: ₹0.8L (optimization)",
            ],
            notifications: [
              "Notify Site Engineer - Material optimization",
              "Alert Procurement Team - Waste reduction plan",
            ],
            costSavings: "₹0.8L (optimization + waste reduction)",
          },
          {
            step: "Alternate Sourcing Strategy",
            details: [
              "Identify 1-2 alternate suppliers",
              "Compare pricing for key materials",
              "Trial orders for cost-effective alternatives",
              "Expected savings: ₹0.5L (alternate sourcing)",
            ],
            notifications: [
              "Notify Procurement Team - Alternate sourcing active",
            ],
            costSavings: "₹0.5L (alternate suppliers)",
          },
        ],
        responsiblePerson: "Procurement Manager",
        status: "Pending",
      },
    ],
    "5-2": [
      {
        riskId: "MODULE-RISK-006",
        project: "Opéra Sky - Work Orders (Labor)",
        riskType: "Module Forecast Overrun",
        detectionLogic: "Module Forecast Overrun > ₹0",
        impactArea: "Labor Budget",
        quantifiedImpact: 235000,
        moduleTrigger: "Work Orders (Labor)",
        automatedMitigation: "Labor rate control + Productivity improvement",
        automatedMitigationSteps: [
          {
            step: "Labor Cost Analysis",
            details: [
              "Current Budget: ₹1.50 Cr",
              "Accrued Value: ₹1.32 Cr (87% utilization)",
              "Forecast Overrun: ₹2.35L",
              "Action: Proactive cost management",
            ],
          },
          {
            step: "Labor Rate Control",
            details: [
              "Review active work order rates",
              "Control overtime costs",
              "Optimize crew deployment",
              "Target savings: ₹1.5L (rate control)",
            ],
            notifications: [
              "Notify Site Supervisor - Rate control measures",
              "Alert Project Manager - Labor cost optimization",
            ],
            costSavings: "₹1.5L (rate control + overtime management)",
          },
          {
            step: "Productivity Improvement",
            details: [
              "Better work planning and scheduling",
              "Reduce idle time",
              "Improve tool/equipment availability",
              "Target savings: ₹0.85L (productivity)",
            ],
            notifications: [
              "Notify Project Team - Productivity program",
              "Alert Site Engineer - Work planning optimization",
            ],
            costSavings: "₹0.85L (productivity improvement)",
          },
        ],
        responsiblePerson: "Project Manager + Site Supervisor",
        status: "Pending",
      },
    ],
  };

  // Helper function to parse currency string to number
  const parseCurrencyToNumber = (currencyString: string): number => {
    if (!currencyString) return 0;
    // Remove currency symbol (₹), commas, and spaces, then parse
    const cleaned = currencyString.replace(/[₹,\s]/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Helper function to parse percentage string to number
  const parsePercentageToNumber = (percentageString: string): number => {
    if (!percentageString) return 0;
    const cleaned = percentageString.replace(/[%,\s]/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  const fetchProjectWiseBudgetSummary = async (projectKey: string, orgId?: string, siteId?: string) => {
    try {
      setModuleLoading(prev => ({ ...prev, [projectKey]: true }));

      const params: any = {
        orgIds: orgId
      };

      // Add siteId if available
      if (siteId) {
        params.siteIds = siteId;
      }

      const response = await controlTowerApis.getProjectWiseBudgetSummary(params);

      if (response?.status && response?.response?.data && Array.isArray(response.response.data)) {
        const mappedData: ModuleBudgetDistribution[] = response.response.data.map((item: any, index: number) => {
          const budget = parseCurrencyToNumber(item["Budget (₹)"] || "0");
          const committed = parseCurrencyToNumber(item["Committed (₹)"] || "0");
          const accrued = parseCurrencyToNumber(item["Accrued (₹)"] || "0");
          const paid = parseCurrencyToNumber(item["Paid (₹)"] || "0");
          const variance = parseCurrencyToNumber(item["Variance (₹)"] || "0");

          const utilizationStr = item["Utilization %"] || "0";
          const utilizationPercent = parsePercentageToNumber(utilizationStr);

          const forecastOverrun = parseCurrencyToNumber(item["Forecast Overrun (₹)"] || "0");

          const pendingWorkValue = committed - paid;

          const percentOfTotal = 0;

          // Map flag
          let flag: "🔴" | "🟠" | "🟢" = "🟢";
          if (item.Flag?.toLowerCase() === "red") flag = "🔴";
          else if (item.Flag?.toLowerCase() === "amber" || item.Flag?.toLowerCase() === "orange") flag = "🟠";

          return {
            key: `${projectKey}-${item.Module || index}-${Date.now()}`,
            module: item.Module || "-",
            budget,
            committed,
            accrued,
            paid,
            pendingWorkValue,
            variance,
            utilizationPercent,
            forecastOverrun,
            percentOfTotal,
            flag,
          };
        });

        setModuleBudgetData(prev => ({
          ...prev,
          [projectKey]: mappedData
        }));
      } else {
        console.warn("No module data found in API response, using mock data");
        const mockData = projectModuleBudgets[projectKey] || [];
        setModuleBudgetData(prev => ({
          ...prev,
          [projectKey]: mockData
        }));
      }
    } catch (error) {
      console.error("Error fetching project-wise budget summary:", error);
      const mockData = projectModuleBudgets[projectKey] || [];
      setModuleBudgetData(prev => ({
        ...prev,
        [projectKey]: mockData
      }));
    } finally {
      setModuleLoading(prev => ({ ...prev, [projectKey]: false }));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch executive summary
        const executiveSummaryResponse = await controlTowerApis.getExecutiveSummaryBudget();
        if (executiveSummaryResponse?.status && executiveSummaryResponse?.response?.data?.length > 0) {
          setExecutiveSummaryData(executiveSummaryResponse.response.data[0]);
        }

        // Fetch project-wise summary
        const projectWiseResponse = await controlTowerApis.getProjectWiseSummary();
        if (projectWiseResponse?.status && projectWiseResponse?.response?.data?.length > 0) {
          const mappedData: ProjectBudgetStatus[] = projectWiseResponse.response.data.map(
            (item: any, index: number) => ({
              key: `${item.project}-${item.orgId}-${item.siteId}-${index}`,
              project: item.project || "NA",
              budget: parseCurrencyToNumber(item.budget || "0"),
              committed: parseCurrencyToNumber(item.committed || "0"),
              accrued: parseCurrencyToNumber(item.accrued || "0"),
              paid: parseCurrencyToNumber(item.paid || "0"),
              pendingWorkValue: parseCurrencyToNumber(item.pendingWorkValue || "0"),
              variance: parseCurrencyToNumber(item.variance || "0"),
              utilizationPercent: parseFloat(item.utilizationPercentage || "0"),
              forecastOverrun: parseCurrencyToNumber(item.forecastOverrun || "0"),
              flag: mapFlagToEmoji(item.flag || "Green"),
              orgId: item.orgId,
              siteId: item.siteId,
            })
          );
          setProjectBudgetStatuses(mappedData);
        } else {
          // Fallback to mock data if API fails
          setProjectBudgetStatuses(mockProjectBudgetStatuses);
        }

        // Fetch budget timeline utilization graph
        const timelineResponse = await controlTowerApis.getBudgetTimelineUtilizationGraph();
        if (timelineResponse?.status && timelineResponse?.response?.data?.length > 0) {
          const data = timelineResponse.response.data;

          // Sort data by year and month (oldest first)
          const sortedData = [...data].sort((a: any, b: any) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.month - b.month;
          });

          // Extract month abbreviations (first 3 letters)
          const months = sortedData.map((item: any) => {
            const monthName = item.month_name || "";
            return monthName.substring(0, 3);
          });

          // Extract percentage values
          const plannedUtilization = sortedData.map((item: any) =>
            parsePercentageToNumber(item.plannedUtilizationPercentage || "0")
          );
          const actualAccrual = sortedData.map((item: any) =>
            parsePercentageToNumber(item.actualAccrualPercentage || "0")
          );
          const forecast = sortedData.map((item: any) =>
            parsePercentageToNumber(item.forecastPercentage || "0")
          );

          // Calculate warnings/alerts
          const warnings: Array<{ type: string; month: string; message: string; value: number; difference?: number }> = [];

          sortedData.forEach((item: any, index: number) => {
            const planned = parsePercentageToNumber(item.plannedUtilizationPercentage || "0");
            const actual = parsePercentageToNumber(item.actualAccrualPercentage || "0");
            const forecastVal = parsePercentageToNumber(item.forecastPercentage || "0");
            const monthName = item.month_name?.substring(0, 3) || "";

            // Early expenditure flag (actual > planned by 15%)
            if (actual > planned && (actual - planned) > 15) {
              const difference = actual - planned;
              warnings.push({
                type: "early",
                month: monthName,
                message: `Actual utilization is ${difference.toFixed(1)}% ahead of planned`,
                value: actual,
                difference: difference,
              });
            }

            // Budget overrun (forecast > 100%)
            if (forecastVal > 100 && item.Status === "Overrun") {
              warnings.push({
                type: "overrun",
                month: monthName,
                message: `Forecast exceeds 100% budget limit`,
                value: forecastVal,
              });
            }
          });

          setTimelineUtilizationData({
            months,
            plannedUtilization,
            actualAccrual,
            forecast,
            warnings,
          });
        }

        // Fetch project-level financial risk summary
        const financialRiskResponse = await controlTowerApis.getProjectLevelFinancialRisk();
        if (financialRiskResponse?.status && financialRiskResponse?.response?.data?.length > 0) {
          const mappedData: ProjectFinancialRisk[] = financialRiskResponse.response.data.map(
            (item: any, index: number) => {
              const currentVariance = parseCurrencyToNumber(item["Current Variance (₹)"] || "0");
              const forecastOverrun = parseCurrencyToNumber(item["Forecast Overrun (₹)"] || "0");
              const procurementCostDrift = parsePercentageToNumber(item["Procurement Cost Drift"] || "0");
              const driftAmount = parseCurrencyToNumber(item["Drift Amount"] || "0");
              const paymentDelayImpact = parsePercentageToNumber(item["Payment Delay Impact"] || "0");
              const delayAmount = parseCurrencyToNumber(item["Delay Amount"] || "0");
              const overheadRate = parseCurrencyToNumber(item["Overhead Rate (₹/day)"] || "0");
              const duration = item["Duration (days)"] || 0;
              const totalRiskExposure = parseCurrencyToNumber(item["Total Risk Exposure (₹)"] || "0");
              const flag = mapFlagToEmoji(item["Flag"] || "Green");

              // Calculate committed value from drift amount and procurement cost drift
              // committed = Drift Amount / (Procurement Cost Drift / 100)
              const committed = procurementCostDrift > 0
                ? driftAmount / (procurementCostDrift / 100)
                : 0;

              return {
                key: `${item["Project"]}-${index}`,
                project: item["Project"] || "NA",
                currentVariance,
                forecastOverrun,
                procurementCostDrift,
                committed,
                paymentDelayImpact,
                overheadRate,
                duration,
                totalRiskExposure,
                flag,
              };
            }
          );
          setProjectFinancialRisks(mappedData);
        } else {
          // Fallback to mock data if API fails
          setProjectFinancialRisks(mockProjectFinancialRisks);
        }

        // Fetch executive insights
        const executiveInsightsResponse = await controlTowerApis.getExecutiveInsights();
        if (executiveInsightsResponse?.status && executiveInsightsResponse?.response?.data?.length > 0) {
          const insightText = executiveInsightsResponse.response.data[0]["Executive Insight"] || "";
          setExecutiveInsight(insightText);
        }
      } catch (error) {
        console.error("Error fetching budget data:", error);
        // Fallback to mock data on error
        setProjectBudgetStatuses(mockProjectBudgetStatuses);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const projectBudgetColumns = [
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      width: 150,
      fixed: "left" as const,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Budget (₹)",
      dataIndex: "budget",
      key: "budget",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Committed (₹)",
      dataIndex: "committed",
      key: "committed",
      width: 140,
      align: "right" as const,
      render: (val: number) => <span>{formatCurrency(val, "INR")}</span>,
    },
    {
      title: "Accrued (₹)",
      dataIndex: "accrued",
      key: "accrued",
      width: 130,
      align: "right" as const,
      render: (val: number) => <span>{formatCurrency(val, "INR")}</span>,
    },
    {
      title: "Paid (₹)",
      dataIndex: "paid",
      key: "paid",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ color: "#52c41a" }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Pending Work Value (₹)",
      dataIndex: "pendingWorkValue",
      key: "pendingWorkValue",
      width: 160,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Variance (₹)",
      dataIndex: "variance",
      key: "variance",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{ fontWeight: 600, color: val < 0 ? "#ff4d4f" : "#52c41a" }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Utilization %",
      dataIndex: "utilizationPercent",
      key: "utilizationPercent",
      width: 140,
      render: (val: number, record: ProjectBudgetStatus) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Progress
            percent={val > 100 ? 100 : val}
            size="small"
            strokeColor={
              val > 95 ? "#ff4d4f" : val > 85 ? "#faad14" : "#52c41a"
            }
            style={{ width: 70 }}
            status={val > 100 ? "exception" : "normal"}
          />
          <Tag
            color={val > 95 ? "red" : val > 85 ? "orange" : "green"}
            style={{
              cursor: val > 85 ? "pointer" : "default",
              fontWeight: 600,
            }}
            onClick={() => {
              if (val > 85) {
                const risks = mockRiskData[record.key] || [];
                setSelectedRisks(risks);
                setRiskDrawerVisible(true);
              }
            }}
          >
            {val}%
          </Tag>
        </div>
      ),
    },
    {
      title: "Forecast Overrun (₹)",
      dataIndex: "forecastOverrun",
      key: "forecastOverrun",
      width: 150,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{ fontWeight: 600, color: val > 0 ? "#ff4d4f" : "#52c41a" }}
        >
          {val === 0 ? "₹0.00" : formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string) => <span style={{ fontSize: 18 }}>{flag}</span>,
    },
  ];

  // Columns for module-wise budget distribution in expandable rows
  const moduleExpandedColumns = [
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: 150,
      fixed: "left" as const,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Budget (₹)",
      dataIndex: "budget",
      key: "budget",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Committed (₹)",
      dataIndex: "committed",
      key: "committed",
      width: 140,
      align: "right" as const,
      render: (val: number) => <span>{formatCurrency(val, "INR")}</span>,
    },
    {
      title: "Accrued (₹)",
      dataIndex: "accrued",
      key: "accrued",
      width: 130,
      align: "right" as const,
      render: (val: number) => <span>{formatCurrency(val, "INR")}</span>,
    },
    {
      title: "Paid (₹)",
      dataIndex: "paid",
      key: "paid",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ color: "#52c41a" }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Pending Work Value (₹)",
      dataIndex: "pendingWorkValue",
      key: "pendingWorkValue",
      width: 160,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Variance (₹)",
      dataIndex: "variance",
      key: "variance",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{ fontWeight: 600, color: val < 0 ? "#ff4d4f" : "#52c41a" }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Utilization %",
      dataIndex: "utilizationPercent",
      key: "utilizationPercent",
      width: 140,
      render: (val: number) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Progress
            percent={val > 100 ? 100 : val}
            size="small"
            strokeColor={
              val > 95 ? "#ff4d4f" : val > 85 ? "#faad14" : "#52c41a"
            }
            style={{ width: 70 }}
            status={val > 100 ? "exception" : "normal"}
          />
          <Tag
            color={val > 95 ? "red" : val > 85 ? "orange" : "green"}
            style={{ fontWeight: 600 }}
          >
            {val}%
          </Tag>
        </div>
      ),
    },
    {
      title: "Forecast Overrun (₹)",
      dataIndex: "forecastOverrun",
      key: "forecastOverrun",
      width: 150,
      align: "right" as const,
      render: (val: number, record: ModuleBudgetDistribution) => {
        const hasRiskData = val > 0 && mockModuleRiskData[record.key];
        return (
          <span
            style={{
              fontWeight: 600,
              color: val > 0 ? "#ff4d4f" : "#52c41a",
              cursor: hasRiskData ? "pointer" : "default",
              textDecoration: hasRiskData ? "underline" : "none",
            }}
            onClick={() => {
              if (hasRiskData) {
                const risks = mockModuleRiskData[record.key] || [];
                setSelectedRisks(risks);
                setRiskDrawerVisible(true);
              }
            }}
          >
            {val === 0 ? "₹0.00" : formatCurrency(val, "INR")}

          </span>
        );
      },
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string) => <span style={{ fontSize: 18 }}>{flag}</span>,
    },
  ];

  const moduleBudgetColumns = [
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: 220,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Committed (₹)",
      dataIndex: "committed",
      key: "committed",
      width: 150,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Accrued (₹)",
      dataIndex: "accrued",
      key: "accrued",
      width: 140,
      align: "right" as const,
      render: (val: number) => <span>{formatCurrency(val, "INR")}</span>,
    },
    {
      title: "% of Total",
      dataIndex: "percentOfTotal",
      key: "percentOfTotal",
      width: 150,
      render: (val: number) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Progress
            percent={val}
            size="small"
            strokeColor={
              val > 50 ? "#ff4d4f" : val > 40 ? "#faad14" : "#52c41a"
            }
            style={{ width: 80 }}
          />
          {/* <span style={{ fontSize: 12, fontWeight: 600 }}>{val}%</span> */}
        </div>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string) => <span style={{ fontSize: 18 }}>{flag}</span>,
    },
  ];

  const financialRiskColumns = [
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      width: 150,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Current Variance (₹)",
      dataIndex: "currentVariance",
      key: "currentVariance",
      width: 150,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{ fontWeight: 600, color: val < 0 ? "#ff4d4f" : "#52c41a" }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Forecast Overrun (₹)",
      dataIndex: "forecastOverrun",
      key: "forecastOverrun",
      width: 150,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{ fontWeight: 600, color: val > 0 ? "#ff4d4f" : "#52c41a" }}
        >
          {val === 0 ? "₹0.00" : formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Procurement Cost Drift",
      dataIndex: "procurementCostDrift",
      key: "procurementCostDrift",
      width: 200,
      align: "right" as const,
      render: (val: number, record: ProjectFinancialRisk) => {
        const monetaryImpact = (val / 100) * record.committed;
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
            <Tag color={val > 3 ? "red" : val > 0 ? "orange" : "green"}>
              {val}%
            </Tag>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#595959" }}>
              {formatCurrency(monetaryImpact, "INR")}
            </span>
          </div>
        );
      },
    },
    {
      title: "Payment Delay Impact",
      dataIndex: "paymentDelayImpact",
      key: "paymentDelayImpact",
      width: 200,
      align: "right" as const,
      render: (val: number, record: ProjectFinancialRisk) => {
        const monetaryImpact = (val / 100) * record.overheadRate * record.duration;
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
            <Tag color={val > 15 ? "red" : val > 10 ? "orange" : "green"}>
              {val}%
            </Tag>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#595959" }}>
              {formatCurrency(monetaryImpact, "INR")}
            </span>
          </div>
        );
      },
    },
    {
      title: "Overhead Rate (₹/day)",
      dataIndex: "overheadRate",
      key: "overheadRate",
      width: 140,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Duration (days)",
      dataIndex: "duration",
      key: "duration",
      width: 120,
      align: "center" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#595959" }}>{val}</span>
      ),
    },
    {
      title: "Total Risk Exposure (₹)",
      dataIndex: "totalRiskExposure",
      key: "totalRiskExposure",
      width: 180,
      align: "right" as const,
      render: (val: number) => (
        <motion.span
          style={{ fontWeight: 700, color: "#ff4d4f" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {formatCurrency(val, "INR")}
        </motion.span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string) => <span style={{ fontSize: 18 }}>{flag}</span>,
    },
  ];

  // Calculate executive summary metrics from API data or fallback to mock data
  const totalBudget = executiveSummaryData
    ? parseCurrencyToNumber(executiveSummaryData.totalBudget)
    : projectBudgetStatuses.reduce((sum, p) => sum + p.budget, 0);

  const committedValue = executiveSummaryData
    ? parseCurrencyToNumber(executiveSummaryData.committedValue)
    : projectBudgetStatuses.reduce((sum, p) => sum + p.committed, 0);

  const committedPercent = executiveSummaryData
    ? parsePercentageToNumber(executiveSummaryData.committedPercentage).toFixed(1)
    : totalBudget > 0 ? ((committedValue / totalBudget) * 100).toFixed(1) : "0.0";

  const accruedValue = executiveSummaryData
    ? parseCurrencyToNumber(executiveSummaryData.accruedValue)
    : projectBudgetStatuses.reduce((sum, p) => sum + p.accrued, 0);

  const accruedPercent = executiveSummaryData
    ? parsePercentageToNumber(executiveSummaryData.accruedPercentage).toFixed(1)
    : totalBudget > 0 ? ((accruedValue / totalBudget) * 100).toFixed(1) : "0.0";

  const paidValue = executiveSummaryData
    ? parseCurrencyToNumber(executiveSummaryData.paidValue)
    : projectBudgetStatuses.reduce((sum, p) => sum + p.paid, 0);

  const uncommittedBudget = executiveSummaryData
    ? parseCurrencyToNumber(executiveSummaryData.uncommittedBudget)
    : totalBudget - committedValue;

  const uncommittedPercent = executiveSummaryData
    ? parsePercentageToNumber(executiveSummaryData.uncommittedPercentage).toFixed(1)
    : totalBudget > 0 ? ((uncommittedBudget / totalBudget) * 100).toFixed(1) : "0.0";

  const variance = executiveSummaryData
    ? parseCurrencyToNumber(executiveSummaryData.variance)
    : totalBudget - accruedValue;

  const variancePercent = executiveSummaryData
    ? parsePercentageToNumber(executiveSummaryData.variancePercentage).toFixed(1)
    : totalBudget > 0 ? ((variance / totalBudget) * 100).toFixed(1) : "0.0";

  const forecastOverrun = executiveSummaryData
    ? parseCurrencyToNumber(executiveSummaryData.forecastOverrun)
    : projectBudgetStatuses.reduce((sum, p) => sum + p.forecastOverrun, 0);

  const forecastOverrunPercent = executiveSummaryData
    ? parsePercentageToNumber(executiveSummaryData.forecastOverrunPercentage).toFixed(1)
    : totalBudget > 0 ? ((forecastOverrun / totalBudget) * 100).toFixed(1) : "0.0";

  // Use accruedPercentage for cost utilization (costUtilization field seems incorrectly formatted)
  const costUtilization = executiveSummaryData
    ? parsePercentageToNumber(executiveSummaryData.accruedPercentage).toFixed(1)
    : totalBudget > 0 ? ((accruedValue / totalBudget) * 100).toFixed(1) : "0.0";

  return (
    <>
      <Head title="Budget Control Tower (By Project)" />
      <Content>
        <BlockHead size="sm">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Budget Control Tower (By Project)
              </BlockTitle>
              <p style={{ color: "#8c8c8c", marginTop: 8 }}>
                Complete financial control and foresight across all projects -
                tracking budget allocation, commitments, accruals, payments, and
                risk exposure
              </p>
            </BlockHeadContent>
          </motion.div>
        </BlockHead>

        <Card className="card-bordered" style={{ overflow: "hidden" }}>
          <div className="card-aside-wrap" style={{ overflow: "hidden" }}>
            <div
              className="card-inner card-inner-lg"
              style={{ overflowX: "auto" }}
            >
              {/* Executive Summary */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Executive Summary
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={2} />
                ) : (
                  <Table
                    dataSource={[
                      {
                        key: "1",
                        metric: "Total Budget (₹)",
                        value: formatCurrency(totalBudget, "INR"),
                        percentage: "-",
                        progress: 100,
                        status: "default",
                      },
                      {
                        key: "2",
                        metric: "Committed Value",
                        value: formatCurrency(committedValue, "INR"),
                        percentage: `${committedPercent}% of Budget`,
                        progress: Number(committedPercent),
                        status: Number(committedPercent) > 95 ? "exception" : Number(committedPercent) > 85 ? "warning" : "success",
                      },
                      {
                        key: "3",
                        metric: "Uncommitted Budget",
                        value: formatCurrency(uncommittedBudget, "INR"),
                        percentage: `${uncommittedPercent}% of Budget`,
                        progress: Math.abs(Number(uncommittedPercent)),
                        status: Number(uncommittedPercent) < 5 ? "exception" : Number(uncommittedPercent) < 10 ? "warning" : "success",
                      },
                      {
                        key: "4",
                        metric: "Accrued Value",
                        value: formatCurrency(accruedValue, "INR"),
                        percentage: `${accruedPercent}% of Budget`,
                        progress: Number(accruedPercent),
                        status: Number(accruedPercent) > 95 ? "exception" : Number(accruedPercent) > 85 ? "warning" : "active",
                      },
                      {
                        key: "5",
                        metric: "Paid Value (₹)",
                        value: formatCurrency(paidValue, "INR"),
                        percentage: "-",
                        progress: (paidValue / totalBudget) * 100,
                        status: "success",
                      },
                      {
                        key: "6",
                        metric: "Variance",
                        value: formatCurrency(variance, "INR"),
                        percentage: `${variancePercent}% of Budget`,
                        progress: Math.abs(Number(variancePercent)),
                        status: variance < 0 ? "exception" : "success",
                      },
                      {
                        key: "7",
                        metric: "Forecast Overrun (Risk Impact)",
                        value: formatCurrency(forecastOverrun, "INR"),
                        percentage: `${forecastOverrunPercent}% of Budget`,
                        progress: Number(forecastOverrunPercent),
                        status: forecastOverrun > 1000000 ? "exception" : forecastOverrun > 0 ? "warning" : "success",
                      },
                      {
                        key: "8",
                        metric: "Cost Utilization",
                        value: `${costUtilization}%`,
                        percentage: "of Total Budget",
                        progress: Number(costUtilization),
                        status: Number(costUtilization) > 95 ? "exception" : Number(costUtilization) > 85 ? "warning" : "active",
                      },
                    ]}
                    columns={[
                      {
                        title: "Metric",
                        dataIndex: "metric",
                        key: "metric",
                        width: 200,
                        render: (text: string) => (
                          <span style={{ fontWeight: 600, fontSize: 13 }}>
                            {text}
                          </span>
                        ),
                      },
                      {
                        title: "Value",
                        dataIndex: "value",
                        key: "value",
                        width: 180,
                        align: "right" as const,
                        render: (text: string, record: any) => (
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: 16,
                              color:
                                record.status === "exception"
                                  ? "#cf1322"
                                  : record.status === "warning"
                                    ? "#fa8c16"
                                    : record.status === "active"
                                      ? "#1890ff"
                                      : "#52c41a",
                            }}
                          >
                            {text}
                          </span>
                        ),
                      },
                      {
                        title: "Percentage",
                        dataIndex: "percentage",
                        key: "percentage",
                        width: 150,
                        align: "center" as const,
                        render: (text: string, record: any) => (
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 12,
                              color:
                                record.status === "exception"
                                  ? "#cf1322"
                                  : record.status === "warning"
                                    ? "#fa8c16"
                                    : "#595959",
                            }}
                          >
                            {text}
                          </span>
                        ),
                      },
                      {
                        title: "Progress",
                        dataIndex: "progress",
                        key: "progress",
                        width: 200,
                        render: (val: number, record: any) => (
                          <Progress
                            percent={val}
                            size="small"
                            status={record.status}
                            strokeColor={
                              record.status === "exception"
                                ? "#ff4d4f"
                                : record.status === "warning"
                                  ? "#faad14"
                                  : record.status === "active"
                                    ? "#1890ff"
                                    : "#52c41a"
                            }
                          />
                        ),
                      },
                    ]}
                    pagination={false}
                    bordered
                    size="small"
                  />
                )}
              </motion.div>

              <Divider />

              {/* Project-wise Budget Status Summary */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Project-wise Budget Status Summary
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={3} />
                ) : (
                  <Table
                    columns={projectBudgetColumns}
                    dataSource={projectBudgetStatuses}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1400 }}
                    expandable={{
                      expandedRowRender: (record: ProjectBudgetStatus) => {
                        const moduleData = moduleBudgetData[record.key] || [];
                        const isLoading = moduleLoading[record.key];

                        return (
                          <div style={{ margin: "16px 24px" }}>
                            <h6
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                marginBottom: 12,
                                color: "#1890ff",
                              }}
                            >
                              Module-wise Budget Distribution for {record.project}
                            </h6>
                            {isLoading ? (
                              <div style={{ textAlign: "center", padding: "20px" }}>
                                <Spin tip="Loading module data..." />
                              </div>
                            ) : moduleData.length > 0 ? (
                              <Table
                                columns={moduleExpandedColumns}
                                dataSource={moduleData}
                                pagination={false}
                                size="small"
                                bordered
                                scroll={{ x: 1400 }}
                                rowKey="key"
                              />
                            ) : (
                              <div style={{ textAlign: "center", padding: "20px", color: "#8c8c8c" }}>
                                No module data available
                              </div>
                            )}
                          </div>
                        );
                      },
                      expandRowByClick: true,
                      expandedRowKeys: expandedRowKeys,
                      onExpand: async (expanded, record) => {
                        if (expanded) {
                          setExpandedRowKeys([...expandedRowKeys, record.key]);
                          if (!moduleBudgetData[record.key] || moduleBudgetData[record.key].length === 0) {
                            const orgId = record.orgId;
                            const siteId = record.siteId;
                            await fetchProjectWiseBudgetSummary(record.key, orgId, siteId);
                          }
                        } else {
                          setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.key));
                        }
                      },
                    }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Budget Timeline Utilization Graph */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.85 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Budget Timeline Utilization Graph
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={6} />
                ) : timelineUtilizationData ? (
                  <>
                    <ReactECharts
                      option={{
                        title: {
                          text: "Budget Utilization Over Time",
                          left: "center",
                          textStyle: {
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#262626",
                          },
                        },
                        tooltip: {
                          trigger: "axis",
                          axisPointer: {
                            type: "cross",
                            label: {
                              backgroundColor: "#6a7985",
                            },
                          },
                          formatter: (params: any) => {
                            let tooltip = `<strong>${params[0].axisValue}</strong><br/>`;
                            params.forEach((item: any) => {
                              tooltip += `${item.marker} ${item.seriesName}: <strong>${item.value.toFixed(2)}%</strong><br/>`;
                            });
                            return tooltip;
                          },
                        },
                        legend: {
                          data: [
                            "Planned Utilization",
                            "Actual Accrual",
                            "Forecast",
                          ],
                          top: 30,
                          textStyle: {
                            fontSize: 12,
                          },
                        },
                        grid: {
                          left: "3%",
                          right: "4%",
                          bottom: "3%",
                          top: 80,
                          containLabel: true,
                        },
                        xAxis: {
                          type: "category",
                          boundaryGap: false,
                          data: timelineUtilizationData.months,
                          axisLabel: {
                            fontSize: 11,
                          },
                        },
                        yAxis: {
                          type: "value",
                          name: "Utilization %",
                          nameLocation: "middle",
                          nameGap: 50,
                          nameTextStyle: {
                            fontSize: 12,
                            fontWeight: 600,
                          },
                          axisLabel: {
                            formatter: "{value}%",
                            fontSize: 11,
                          },
                          max: Math.max(
                            120,
                            Math.ceil(
                              Math.max(
                                ...timelineUtilizationData.plannedUtilization,
                                ...timelineUtilizationData.actualAccrual,
                                ...timelineUtilizationData.forecast
                              ) / 10
                            ) * 10
                          ),
                        },
                        series: [
                          {
                            name: "Planned Utilization",
                            type: "line",
                            smooth: true,
                            data: timelineUtilizationData.plannedUtilization,
                            lineStyle: {
                              width: 2,
                              color: "#52c41a",
                            },
                            itemStyle: {
                              color: "#52c41a",
                            },
                            areaStyle: {
                              color: {
                                type: "linear",
                                x: 0,
                                y: 0,
                                x2: 0,
                                y2: 1,
                                colorStops: [
                                  {
                                    offset: 0,
                                    color: "rgba(82, 196, 26, 0.15)",
                                  },
                                  {
                                    offset: 1,
                                    color: "rgba(82, 196, 26, 0.01)",
                                  },
                                ],
                              },
                            },
                          },
                          {
                            name: "Actual Accrual",
                            type: "line",
                            smooth: true,
                            data: timelineUtilizationData.actualAccrual,
                            lineStyle: {
                              width: 3,
                              color: "#1890ff",
                            },
                            itemStyle: {
                              color: "#1890ff",
                            },
                            markLine: {
                              data: [{ yAxis: 100, name: "100% Budget" }],
                              lineStyle: {
                                color: "#ff4d4f",
                                type: "dashed",
                                width: 2,
                              },
                              label: {
                                formatter: "Budget Limit",
                                position: "end",
                                fontSize: 11,
                                color: "#ff4d4f",
                                fontWeight: 600,
                              },
                            },
                            markPoint: {
                              data: timelineUtilizationData.warnings
                                .filter((w): w is { type: string; month: string; message: string; value: number; difference: number } =>
                                  w.type === "early" && w.difference !== undefined
                                )
                                .map((warning) => {
                                  return {
                                    name: "Early Expenditure Flag",
                                    coord: [warning.month, warning.value],
                                    value: `⚠️ ${warning.difference.toFixed(1)}% ahead`,
                                    itemStyle: {
                                      color: "#faad14",
                                    },
                                    label: {
                                      fontSize: 10,
                                      color: "#faad14",
                                      fontWeight: 600,
                                      position: "top",
                                    },
                                  };
                                })
                                .concat(
                                  timelineUtilizationData.warnings
                                    .filter((w) => w.type === "overrun")
                                    .map((warning) => {
                                      return {
                                        name: "Budget Overrun",
                                        coord: [warning.month, warning.value],
                                        value: "🔴 Overrun",
                                        itemStyle: {
                                          color: "#ff4d4f",
                                        },
                                        label: {
                                          fontSize: 10,
                                          color: "#ff4d4f",
                                          fontWeight: 600,
                                          position: "top",
                                        },
                                      };
                                    })
                                ),
                            },
                          },
                          {
                            name: "Forecast",
                            type: "line",
                            smooth: true,
                            data: timelineUtilizationData.forecast,
                            lineStyle: {
                              width: 2,
                              type: "dashed",
                              color: "#faad14",
                            },
                            itemStyle: {
                              color: "#faad14",
                            },
                          },
                        ],
                      }}
                      style={{ height: "450px", width: "100%" }}
                      notMerge={true}
                      lazyUpdate={true}
                    />
                    {timelineUtilizationData.warnings.length > 0 && (
                      <div
                        style={{
                          marginTop: 16,
                          padding: "12px 16px",
                          background: "#fff7e6",
                          borderRadius: 6,
                          borderLeft: "3px solid #faad14",
                        }}
                      >
                        <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                          {timelineUtilizationData.warnings
                            .filter((w) => w.type === "early")
                            .map((warning, index) => (
                              <li
                                key={`early-${index}`}
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 6,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <WarningOutlined
                                  style={{ color: "#faad14", fontSize: 13 }}
                                />
                                <strong>Early Expenditure Flag:</strong>{" "}
                                {warning.message} in {warning.month} (exceeds 15%
                                threshold)
                              </li>
                            ))}
                          {timelineUtilizationData.warnings
                            .filter((w) => w.type === "overrun")
                            .map((warning, index) => (
                              <li
                                key={`overrun-${index}`}
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: index === timelineUtilizationData.warnings.filter((w) => w.type === "overrun").length - 1 ? 0 : 6,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <WarningOutlined
                                  style={{ color: "#ff4d4f", fontSize: 13 }}
                                />
                                <strong>Probable Overrun:</strong> Forecast
                                crosses <strong>100% budget limit</strong> in{" "}
                                {warning.month} (before project completion)
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ padding: "20px", textAlign: "center", color: "#8c8c8c" }}>
                    No timeline utilization data available
                  </div>
                )}
              </motion.div>

              <Divider />

              {/* Project-level Financial Risk Summary */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.0 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Project-level Financial Risk Summary
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={3} />
                ) : (
                  <Table
                    columns={financialRiskColumns}
                    dataSource={projectFinancialRisks}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1300 }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Business Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                style={{ marginTop: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Executive Insights
                </h5>

                {loading ? (
                  <div
                    style={{
                      marginBottom: 20,
                      padding: "12px 16px",
                      background: "#fafafa",
                      borderRadius: 6,
                      borderLeft: "3px solid #faad14",
                    }}
                  >
                    <Spin size="small" />
                  </div>
                ) : executiveInsight ? (
                  <div
                    style={{
                      marginBottom: 20,
                      padding: "12px 16px",
                      background: "#fafafa",
                      borderRadius: 6,
                      borderLeft: "3px solid #faad14",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 13,
                        marginBottom: 0,
                        lineHeight: 1.7,
                        color: "#595959",
                      }}
                    >
                      {executiveInsight}
                    </p>
                  </div>
                ) : null}

                <div style={{ marginLeft: -8, marginRight: -8 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <div
                        style={{
                          padding: "16px",
                          background: "#fff",
                          borderRadius: 6,
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginBottom: 12,
                            color: "#262626",
                          }}
                        >
                          Critical Financial Risks
                        </h6>
                        <ul
                          style={{ margin: 0, padding: 0, listStyle: "none" }}
                        >
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <WarningOutlined
                              style={{ color: "#ff4d4f", fontSize: 14 }}
                            />
                            <strong>Elysium Towers forecasted 10% overrun</strong>{" "}
                            → ₹30L risk
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <WarningOutlined
                              style={{ color: "#faad14", fontSize: 14 }}
                            />
                            <strong>
                              Paramount Rise nearing limit (93% utilized)
                            </strong>{" "}
                            → ₹10L possible overrun
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <WarningOutlined
                              style={{ color: "#ff4d4f", fontSize: 14 }}
                            />
                            <strong>Quality & Rework cost {">"}3%</strong> →
                            Trigger QA/Project audit
                          </li>
                        </ul>
                      </div>
                    </Col>
                    <Col xs={24} lg={12}>
                      <div
                        style={{
                          padding: "16px",
                          background: "#fff",
                          borderRadius: 6,
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginBottom: 12,
                            color: "#262626",
                          }}
                        >
                          Key Actions Required
                        </h6>
                        <ul
                          style={{ margin: 0, padding: 0, listStyle: "none" }}
                        >
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 14 }}
                            />
                            Freeze new WOs/PRs on Elysium Towers until budget
                            review
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 14 }}
                            />
                            Restructure procurement rate contracts (5% drift
                            detected)
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 14 }}
                            />
                            Hold low-priority POs on Paramount Rise (₹20L deferred)
                          </li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </div>
              </motion.div>
            </div>
          </div>
        </Card>
      </Content>

      {/* Risk Interlinking Drawer */}
      <Drawer
        title={
          <span>
            <RobotOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            Budget Risk Interlinking & Automated Mitigation
          </span>
        }
        placement="right"
        onClose={() => setRiskDrawerVisible(false)}
        open={riskDrawerVisible}
        width="85%"
        style={{ maxWidth: 1400 }}
      >
        {selectedRisks.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="Project" span={2}>
                <strong>{selectedRisks[0].project}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Risk Type" span={2}>
                <Tag color="orange" style={{ fontSize: 12 }}>
                  {selectedRisks[0].riskType}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Impact Area" span={2}>
                {selectedRisks[0].impactArea}
              </Descriptions.Item>
              <Descriptions.Item label="Module Trigger" span={2}>
                {selectedRisks[0].moduleTrigger}
              </Descriptions.Item>
              <Descriptions.Item label="Quantified Impact" span={2}>
                <strong style={{ color: "#ff4d4f", fontSize: 14 }}>
                  {formatCurrency(selectedRisks[0].quantifiedImpact, "INR")}
                </strong>
              </Descriptions.Item>
            </Descriptions>

            <Divider>Budget Risk with Interlinking</Divider>

            <Table
              columns={[
                {
                  title: "Risk ID",
                  dataIndex: "riskId",
                  key: "riskId",
                  width: 150,
                  render: (text: string) => (
                    <span style={{ fontWeight: 600 }}>{text}</span>
                  ),
                },
                {
                  title: "Risk Type",
                  dataIndex: "riskType",
                  key: "riskType",
                  width: 180,
                  render: (text: string) => (
                    <Tag color="orange" style={{ fontSize: 11 }}>
                      {text}
                    </Tag>
                  ),
                },
                {
                  title: "Detection Logic",
                  dataIndex: "detectionLogic",
                  key: "detectionLogic",
                  width: 150,
                },
                {
                  title: "Impact Area",
                  dataIndex: "impactArea",
                  key: "impactArea",
                  width: 200,
                },
                {
                  title: "Quantified Impact",
                  dataIndex: "quantifiedImpact",
                  key: "quantifiedImpact",
                  width: 150,
                  align: "right" as const,
                  render: (val: number) => (
                    <span style={{ fontWeight: 600, color: "#ff4d4f" }}>
                      {formatCurrency(val, "INR")}
                    </span>
                  ),
                },
                {
                  title: "Responsible Person",
                  dataIndex: "responsiblePerson",
                  key: "responsiblePerson",
                  width: 160,
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: 100,
                  render: (status: string) => (
                    <Tag
                      color={
                        status === "Active"
                          ? "red"
                          : status === "Mitigated"
                            ? "green"
                            : "orange"
                      }
                    >
                      {status}
                    </Tag>
                  ),
                },
              ]}
              dataSource={selectedRisks}
              rowKey="riskId"
              pagination={false}
              scroll={{ x: 1200 }}
              expandable={{
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) {
                    setExpandedRowKeys([record.riskId]);
                    setAiLoadingRisks((prev) => ({
                      ...prev,
                      [record.riskId]: true,
                    }));
                    setAiLoadingTexts((prev) => ({
                      ...prev,
                      [record.riskId]: aiLoadingMessages[0],
                    }));

                    let messageIndex = 0;
                    const messageInterval = setInterval(() => {
                      messageIndex =
                        (messageIndex + 1) % aiLoadingMessages.length;
                      setAiLoadingTexts((prev) => ({
                        ...prev,
                        [record.riskId]: aiLoadingMessages[messageIndex],
                      }));
                    }, 800);

                    setTimeout(() => {
                      clearInterval(messageInterval);
                      setAiLoadingRisks((prev) => ({
                        ...prev,
                        [record.riskId]: false,
                      }));
                    }, 2500);
                  } else {
                    setExpandedRowKeys([]);
                  }
                },
                expandedRowRender: (record) => (
                  <div
                    style={{
                      padding: "16px",
                      background: "#fafafa",
                      borderRadius: 8,
                    }}
                  >
                    <h6
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        marginBottom: 12,
                        color: "#262626",
                      }}
                    >
                      <RobotOutlined
                        style={{ marginRight: 6, color: "#1890ff" }}
                      />
                      Automated Mitigation
                    </h6>

                    {aiLoadingRisks[record.riskId] ? (
                      <div style={{ textAlign: "center", padding: "32px 0" }}>
                        <Spin
                          indicator={
                            <LoadingOutlined
                              style={{ fontSize: 32, color: "#1890ff" }}
                              spin
                            />
                          }
                        />
                        <p
                          style={{
                            marginTop: 16,
                            color: "#8c8c8c",
                            fontSize: 13,
                          }}
                        >
                          {aiLoadingTexts[record.riskId] || "Processing..."}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            marginBottom: 16,
                            padding: "12px 16px",
                            background: "#e6f7ff",
                            borderRadius: 6,
                            borderLeft: "3px solid #1890ff",
                          }}
                        >
                          <p
                            style={{
                              fontSize: 13,
                              marginBottom: 0,
                              lineHeight: 1.6,
                              color: "#262626",
                            }}
                          >
                            <strong>Strategy:</strong>{" "}
                            {record.automatedMitigation}
                          </p>
                        </div>

                        {record.automatedMitigationSteps ? (
                          <div style={{ marginTop: 8 }}>
                            {record.automatedMitigationSteps.map(
                              (stepData, index) => (
                                <div
                                  key={index}
                                  style={{
                                    marginBottom: 16,
                                    paddingBottom: 12,
                                    borderBottom:
                                      index <
                                        record.automatedMitigationSteps!.length -
                                        1
                                        ? "1px dashed #e8e8e8"
                                        : "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: 8,
                                      gap: 8,
                                    }}
                                  >
                                    <strong
                                      style={{ fontSize: 13, color: "#262626" }}
                                    >
                                      {stepData.step}
                                    </strong>
                                  </div>

                                  {stepData.details && (
                                    <ul
                                      style={{
                                        margin: "8px 0 0 24px",
                                        padding: 0,
                                        listStyle: "disc",
                                      }}
                                    >
                                      {stepData.details.map((detail, idx) => (
                                        <li
                                          key={idx}
                                          style={{
                                            fontSize: 12,
                                            color: "#595959",
                                            marginBottom: 4,
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {detail}
                                        </li>
                                      ))}
                                    </ul>
                                  )}

                                  {stepData.notifications && (
                                    <div
                                      style={{ marginTop: 8, marginLeft: 24 }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "#595959",
                                          marginBottom: 6,
                                        }}
                                      >
                                        Actions & Notifications
                                      </div>
                                      <ul
                                        style={{
                                          margin: 0,
                                          padding: 0,
                                          listStyle: "none",
                                        }}
                                      >
                                        {stepData.notifications.map(
                                          (notification, idx) => (
                                            <li
                                              key={idx}
                                              style={{
                                                fontSize: 12,
                                                color: "#595959",
                                                marginBottom: 6,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                              }}
                                            >
                                              <BellOutlined
                                                style={{
                                                  fontSize: 12,
                                                  color: "#1890ff",
                                                }}
                                              />
                                              {notification}
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                  {stepData.costSavings && (
                                    <div
                                      style={{
                                        marginTop: 8,
                                        marginLeft: 24,
                                        padding: "6px 12px",
                                        background: "#f6ffed",
                                        borderRadius: 4,
                                        border: "1px solid #b7eb8f",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "#52c41a",
                                        }}
                                      >
                                        💰 Cost Savings: {stepData.costSavings}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <p
                            style={{
                              fontSize: 13,
                              color: "#8c8c8c",
                              marginTop: 8,
                            }}
                          >
                            {record.automatedMitigation}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ),
              }}
            />
          </motion.div>
        )}
      </Drawer>
    </>
  );
};

export default BudgetControlTower;
